import { BarChart3, ChevronDown, ChevronLeft, ChevronRight, DollarSign, Home, Layers, Lock, Package, PlusCircle, RefreshCcw, RotateCcw, Settings, Truck, AlertCircle, X, ClipboardList, CircleDollarSign } from "lucide-react";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../consts";
import authService from "../../services/AuthService";
import CatalogPage from "./pages/CatalogPage";
import SettingsPage from "./pages/SettingsPage";
import BatchesPage from "./pages/BatchesPage";
import ShipmentsPage from "./pages/ShipmentsPage";
import OrdersPage from "./pages/OrdersPage";
import ReturnsPage from "./pages/ReturnsPage";
import ReimbursementsPage from "./pages/ReimbursementsPage";
import ReportsHubPage from "./pages/reports/ReportsHubPage";
import WalmartLoginButton from "./components/walmart/Walmartloginbutton";
import { syncWalmartItems, syncInventorySfPage, syncInventoryWfsPage, syncOrdersPage, finalizeOrderSync, captureInventorySnapshot, withPageRetry, getOrderSyncStatus, markOrdersBackfilled, relinkOrphanedOrdersPage } from "./services/catalogService";
import GettingStartedGuide from "./components/common/GettingStartedGuide";
import MonthEndValuationReminder from "./components/reports/MonthEndValuationReminder";

const LAST_ITEM_SYNC_KEY = "msp_last_item_sync_completed_at";
const LAST_INVENTORY_SYNC_KEY = "msp_last_inventory_sync_completed_at";
const LAST_ORDER_SYNC_KEY = "msp_last_order_sync_completed_at";
const SYNC_STALE_MS = 24 * 60 * 60 * 1000; // 24 hours — once a day

const ERROR_SUFFIX = "_had_error";

// A sync that previously failed is ALWAYS treated as stale, regardless of
// the normal staleness window — never wait days to retry something that's
// currently incomplete.
const isStale = (key) => {
  try {
    if (localStorage.getItem(key + ERROR_SUFFIX) === "1") return true;
    const last = localStorage.getItem(key);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) > SYNC_STALE_MS;
  } catch {
    return true;
  }
};

const markCompleted = (key) => {
  try {
    localStorage.setItem(key, Date.now().toString());
    localStorage.removeItem(key + ERROR_SUFFIX);
  } catch { /* ignore */ }
};

const markFailed = (key) => {
  try { localStorage.setItem(key + ERROR_SUFFIX, "1"); } catch { /* ignore */ }
};

// Reads the same timestamp markCompleted() already writes, formatted for
// display next to each sync button — "Never" if it hasn't run yet this
// browser, otherwise a short relative-or-clock string.
const getLastSyncLabel = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return "Never";
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return "Never";
    const diffMs = Date.now() - ts;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "Never";
  }
};

/* ─── Left Sidebar ─── */
const Sidebar = ({
  active, onNavigate, walmartConnected,
  itemSyncStatus, inventorySyncStatus, orderSyncStatus,
  onSyncItems, onSyncInventory, onSyncOrders, onSyncAll,
}) => {
  const sections = [
    { id: "dashboard", icon: Home, label: "Dashboard" },
    { id: "inventory", icon: Package, label: "Catalog" },
    { id: "batches", icon: Layers, label: "Batches / Listing" },
    { id: "shipments", icon: Truck, label: "Shipments" },
    { id: "orders", icon: ClipboardList, label: "Orders" },
    { id: "returns", icon: RotateCcw, label: "Returns" },
    {
      id: "reports",
      icon: BarChart3,
      label: "Reports",
    },
    { id: "reimbursements", icon: CircleDollarSign, label: "Reimbursements" },
    { id: "settings", icon: Settings, label: "Settings" },
  ];

  // Every section except Dashboard/Settings needs a linked Walmart
  // account to mean anything — disabled (dimmed, unclickable, no hover
  // state) until walmartConnected is confirmed true.
  const ALWAYS_ENABLED = new Set(["dashboard", "settings"]);

  const [expanded, setExpanded] = useState({ inventory: true, accounting: false, reports: false });
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={`${collapsed ? "w-16" : "w-64"} bg-white border-r border-gray-200 h-screen sticky top-0 flex flex-col flex-shrink-0 shadow-sm transition-all duration-200 overflow-hidden`}>
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package size={16} className="text-white" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-black font-bold text-sm truncate">MSP Inventory</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="p-2 rounded-lg text-gray-400 bg-teal-100 hover:text-gray-700 hover:bg-teal-200 transition flex-shrink-0"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        {sections.map(({ id, icon: Icon, label, sub }) => {
          const disabled = walmartConnected !== true && !ALWAYS_ENABLED.has(id);
          return (
            <div key={id}>
              <button
                onClick={() => {
                  if (disabled) return;
                  // Always calls onNavigate — including when `id` already
                  // equals `active` — so re-clicking the current section
                  // resets it to its default screen instead of being a no-op.
                  onNavigate(id);
                  if (sub) setExpanded((p) => ({ ...p, [id]: !p[id] }));
                }}
                title={disabled ? "Connect your Walmart account first" : (collapsed ? label : undefined)}
                disabled={disabled}
                className={`w-full flex items-center gap-3 ${collapsed ? "justify-center px-0" : "px-4"} py-2.5 text-sm font-medium transition-all ${disabled
                  ? "text-gray-300 cursor-not-allowed"
                  : active === id
                    ? "bg-teal-50 text-teal-700 border-r-2 border-teal-500"
                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                  }`}
              >
                <Icon size={16} className="flex-shrink-0" />
                {!collapsed && <span className="flex-1 text-left truncate">{label}</span>}
                {!collapsed && sub && <ChevronDown size={14} className={`transition-transform ${expanded[id] ? "rotate-180" : ""}`} />}
              </button>
              {!collapsed && sub && expanded[id] && !disabled && (
                <div className="bg-white">
                  {sub.map((s) => (
                    <button
                      key={s}
                      onClick={() => onNavigate(id)}
                      className="w-full flex items-center gap-3 px-8 py-2 text-xs text-gray-400 hover:text-gray-700 transition"
                    >
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {walmartConnected === true && itemSyncStatus && (
        <SidebarSyncBox
          collapsed={collapsed}
          itemSyncStatus={itemSyncStatus}
          inventorySyncStatus={inventorySyncStatus}
          orderSyncStatus={orderSyncStatus}
          onSyncItems={onSyncItems}
          onSyncInventory={onSyncInventory}
          onSyncOrders={onSyncOrders}
          onSyncAll={onSyncAll}
        />
      )}
    </div>
  );
};

// Always-visible sync panel pinned to the bottom of the sidebar — same
// three statuses AND same trigger actions already on Dashboard, just
// reachable from anywhere without navigating there. Sits below <nav>'s
// own scrollable area (flex-1 overflow-y-auto) and outside it, so it
// never needs its own scroll to stay in view.
const SidebarSyncBox = ({
  collapsed, itemSyncStatus, inventorySyncStatus, orderSyncStatus,
  onSyncItems, onSyncInventory, onSyncOrders, onSyncAll,
}) => {
  const rows = [
    { key: "items", label: "Catalog", status: itemSyncStatus, storageKey: LAST_ITEM_SYNC_KEY, onClick: onSyncItems },
    { key: "inventory", label: "Inventory", status: inventorySyncStatus, storageKey: LAST_INVENTORY_SYNC_KEY, onClick: onSyncInventory },
    { key: "orders", label: "Orders", status: orderSyncStatus, storageKey: LAST_ORDER_SYNC_KEY, onClick: onSyncOrders },
  ];
  const anyRunning = rows.some((r) => r.status.running);

  if (collapsed) {
    const anyError = rows.some((r) => r.status.error);
    return (
      <div className="p-2 border-t border-gray-100 flex-shrink-0 flex items-center justify-center">
        <button onClick={onSyncAll} disabled={anyRunning} title="Sync All"
          className="p-1.5 rounded-lg hover:bg-teal-50 transition disabled:opacity-50">
          <RefreshCcw size={14} className={anyRunning ? "text-teal-500 animate-spin" : anyError ? "text-red-400" : "text-gray-400"} />
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 border-t border-gray-100 flex-shrink-0 mb-32">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-bold text-gray-400 uppercase">Sync Status</p>
        <button onClick={onSyncAll} disabled={anyRunning}
          title="Sync All"
          className="text-gray-400 hover:text-teal-600 transition disabled:opacity-50">
          <RefreshCcw size={12} className={anyRunning ? "animate-spin text-teal-500" : ""} />
        </button>
      </div>
      <div className="space-y-1">
        {rows.map((r) => (
          <button
            key={r.key}
            onClick={r.onClick}
            disabled={r.status.running}
            title={`Sync ${r.label} now`}
            className="w-full flex items-center justify-between text-[11px] py-0.5 rounded hover:bg-gray-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="text-gray-500 flex items-center gap-1">
              {r.status.running && <RefreshCcw size={10} className="text-teal-500 animate-spin" />}
              {!r.status.running && r.status.error && <AlertCircle size={10} className="text-red-400" />}
              {r.label}
            </span>
            <span className={`font-semibold ${r.status.error ? "text-red-500" : r.status.running ? "text-teal-600" : "text-gray-400 group-hover:text-teal-600"}`}>
              {r.status.running ? "Syncing…" : r.status.error ? "Error" : getLastSyncLabel(r.storageKey)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── Dashboard Home ─── */
const DASHBOARD_SECTIONS = [
  { id: "inventory", icon: Package, label: "Catalog" },
  { id: "batches", icon: Layers, label: "Batches / Listing" },
  { id: "shipments", icon: Truck, label: "Shipments" },
  { id: "orders", icon: ClipboardList, label: "Orders" },
  { id: "returns", icon: RotateCcw, label: "Returns" },
  { id: "reports", icon: BarChart3, label: "Reports" },
  { id: "reimbursements", icon: CircleDollarSign, label: "Reimbursements" },
  { id: "settings", icon: Settings, label: "Settings" },
];

const DashboardHome = ({
  onNavigate,
  itemSyncStatus, inventorySyncStatus, orderSyncStatus,
  onSyncItems, onSyncInventory, onSyncOrders, onSyncAll,
  walmartConnected, onWalmartStatusChange,
}) => {
  const anySyncRunning = itemSyncStatus.running || inventorySyncStatus.running || orderSyncStatus.running;

  const syncButtons = [
    { key: "items", label: "Catalog", status: itemSyncStatus, onClick: onSyncItems, storageKey: LAST_ITEM_SYNC_KEY },
    { key: "inventory", label: "Inventory", status: inventorySyncStatus, onClick: onSyncInventory, storageKey: LAST_INVENTORY_SYNC_KEY },
    { key: "orders", label: "Orders", status: orderSyncStatus, onClick: onSyncOrders, storageKey: LAST_ORDER_SYNC_KEY },
  ];

  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">MSP Inventory Dashboard</h1>
      </div>

      {!walmartConnected && (
        <div className="bg-white rounded-2xl shadow-lg p-5 mb-8">
          <WalmartLoginButton onStatusChange={onWalmartStatusChange} />
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg p-5 mb-8">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800 text-sm">Sync with Walmart:</h3>
          <div className="flex flex-wrap gap-6">
            {syncButtons.map((s) => (
              <button
                key={s.key}
                onClick={s.onClick}
                disabled={s.status.running}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-teal-500 text-teal-600 rounded-xl font-bold text-xs hover:bg-teal-50 transition disabled:opacity-50"
              >
                <RefreshCcw size={13} className={s.status.running ? "animate-spin" : ""} />
                {s.status.running ? "Syncing..." : `${s.label} (${getLastSyncLabel(s.storageKey)})`}
                {s.status.error && <AlertCircle size={13} className="text-red-500" />}
              </button>
            ))}
          </div>
          <button
            onClick={onSyncAll}
            disabled={anySyncRunning}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold text-xs hover:opacity-90 transition disabled:opacity-50"
          >
            <RefreshCcw size={14} className={anySyncRunning ? "animate-spin" : ""} /> Sync All
          </button>
        </div>
      </div>

      <GettingStartedGuide short={false} autoOpen={true} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 mt-8">
        {DASHBOARD_SECTIONS.map(({ id, icon: Icon, label }) => {
          // Settings stays reachable regardless (mirrors ALWAYS_ACCESSIBLE_SECTIONS
          // in the parent) — everything else needs a linked Walmart account.
          const disabled = walmartConnected !== true && id !== "settings";
          return (
            <button
              key={id}
              onClick={() => { if (!disabled) onNavigate(id); }}
              disabled={disabled}
              title={disabled ? "Connect your Walmart account first" : undefined}
              className={`bg-white rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center gap-2 border-2 border-transparent transition text-center ${disabled
                ? "opacity-40 cursor-not-allowed"
                : "hover:shadow-xl hover:border-teal-400"
                }`}
            >
              <Icon size={24} className={disabled ? "text-gray-300" : "text-teal-600"} />
              <span className={`font-bold text-sm ${disabled ? "text-gray-400" : "text-gray-800"}`}>{label}</span>
            </button>
          );
        })}
      </div>

      <MonthEndValuationReminder hint="Go to Reports → Inventory Valuation and use its Download button." />
    </div>
  );
};

/* ─── Main Component ─── */
const MSPInventoryPage = ({ user }) => {
  const [sidebar, setSidebar] = useState("dashboard");
  const [quickAddItemId, setQuickAddItemId] = useState(null);
  // Bumped on every sidebar navigation, even a click on the already-active
  // section — used as part of the rendered page's `key` so clicking
  // "Reports" while already deep inside e.g. P&L forces a full remount
  // back to that section's default screen. Without this, clicking the
  // same sidebar item twice does nothing, since setActive(id) with an
  // unchanged id never triggers a re-render on its own.
  const [navKey, setNavKey] = useState(0);

  // null = still checking (avoid a flash of "disconnected" before we know);
  // true/false once WalmartLoginButton reports in. Everything below —
  // background syncing, sidebar access, page content — is gated on this,
  // since nothing in this app is meaningful without a linked Walmart account.
  const [walmartConnected, setWalmartConnected] = useState(null);

  // Dashboard and Settings stay reachable regardless of connection status —
  // Dashboard is where the Connect button lives, and Settings is where the
  // OAuth handoff redirect (see the marketplace=walmart effect below) lands
  // mid-flow, before the connection is confirmed.
  const ALWAYS_ACCESSIBLE_SECTIONS = new Set(["dashboard", "settings"]);

  const navigateTo = useCallback((id) => {
    if (walmartConnected !== true && !ALWAYS_ACCESSIBLE_SECTIONS.has(id)) return; // sidebar disabled until connected
    setSidebar(id);
    setNavKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walmartConnected]);

  // ── Catalog + inventory sync — lives here so it keeps running no matter
  // which sidebar tab you're on within msp-inventory, not just on Catalog. ──
  const [itemSyncStatus, setItemSyncStatus] = useState({
    running: false, itemsSynced: 0, totalItems: null, error: "",
  });
  const [itemSyncPopup, setItemSyncPopup] = useState(false);
  const [inventorySyncStatus, setInventorySyncStatus] = useState({
    running: false,
    sfProcessed: 0, sfTotal: null,
    wfsProcessed: 0, wfsTotal: null,
    error: "",
  });
  const [orderSyncStatus, setOrderSyncStatus] = useState({
    running: false, sfProcessed: 0, sfTotal: null, wfsProcessed: 0, wfsTotal: null, error: "", relinkProgress: null,
  });
  const appSyncStartedRef = useRef(false);

  const runItemsSync = useCallback(async () => {
    setItemSyncStatus({ running: true, itemsSynced: 0, totalItems: null, error: "" });
    setItemSyncPopup(false);
    let cursor = "*";
    let synced = 0;
    let done = false;
    try {
      while (!done) {
        const res = await withPageRetry(() => syncWalmartItems(cursor));
        synced += res.synced || 0;
        setItemSyncStatus((s) => ({ ...s, itemsSynced: synced, totalItems: res.totalItems ?? s.totalItems }));
        cursor = res.next_cursor;
        done = res.done;
      }
      setItemSyncPopup(true);
      markCompleted(LAST_ITEM_SYNC_KEY);
    } catch (e) {
      setItemSyncStatus((s) => ({ ...s, error: e.message || "Item sync failed" }));
      markFailed(LAST_ITEM_SYNC_KEY);
    } finally {
      setItemSyncStatus((s) => ({ ...s, running: false }));
    }
  }, []);

  const runInventorySync = useCallback(async () => {
    setInventorySyncStatus({ running: true, sfProcessed: 0, sfTotal: null, wfsProcessed: 0, wfsTotal: null, error: "" });
    try {
      const runSfLoop = async () => {
        let cursor = undefined;
        let sfDone = false;
        let cursorRestarts = 0;
        while (!sfDone) {
          let res;
          try {
            res = await withPageRetry(() => syncInventorySfPage(cursor), { retryableStatuses: [429, 500, 502, 503, 504] });
          } catch (e) {
            if (e.status === 409 && cursorRestarts < 3) {
              cursorRestarts += 1;
              cursor = undefined;
              setInventorySyncStatus((s) => ({ ...s, sfProcessed: 0 }));
              continue;
            }
            throw e;
          }
          setInventorySyncStatus((s) => ({
            ...s,
            sfProcessed: s.sfProcessed + (res.received || 0),
            sfTotal: res.total_count ?? s.sfTotal,
          }));
          cursor = res.next_cursor;
          sfDone = res.done;
        }
      };

      // WFS inventory has no documented cap on offset (unlike catalog's
      // 10,000 ceiling) and no sequential-only requirement (unlike SF,
      // which Walmart enforces strictly one-at-a-time). Once the first
      // page tells us total_count, fire the rest concurrently in bounded
      // batches instead of one page at a time.
      const WFS_CONCURRENCY = 3;

      const runWfsLoop = async () => {
        const first = await withPageRetry(() => syncInventoryWfsPage(0));
        setInventorySyncStatus((s) => ({
          ...s,
          wfsProcessed: s.wfsProcessed + (first.received || 0),
          wfsTotal: first.total_count ?? s.wfsTotal,
        }));

        if (first.done) return;

        if (!first.total_count) {
          // total_count wasn't returned for some reason — fall back to the
          // original safe sequential walk rather than guessing at offsets.
          let offset = first.next_offset;
          let wfsDone = false;
          while (!wfsDone) {
            const res = await withPageRetry(() => syncInventoryWfsPage(offset));
            setInventorySyncStatus((s) => ({
              ...s,
              wfsProcessed: s.wfsProcessed + (res.received || 0),
              wfsTotal: res.total_count ?? s.wfsTotal,
            }));
            offset = res.next_offset;
            wfsDone = res.done;
          }
          return;
        }

        const pageSize = first.next_offset || 300;
        const offsets = [];
        for (let off = pageSize; off < first.total_count; off += pageSize) offsets.push(off);

        for (let i = 0; i < offsets.length; i += WFS_CONCURRENCY) {
          const batch = offsets.slice(i, i + WFS_CONCURRENCY);
          await Promise.all(batch.map(async (offset) => {
            const res = await withPageRetry(() => syncInventoryWfsPage(offset));
            setInventorySyncStatus((s) => ({
              ...s,
              wfsProcessed: s.wfsProcessed + (res.received || 0),
              wfsTotal: res.total_count ?? s.wfsTotal,
            }));
          }));
        }
      };

      await Promise.all([runSfLoop(), runWfsLoop()]);

      // One extra call, once per sync — records today's stock levels so
      // Reports > SKU Detail can chart stock-over-time going forward.
      try { await captureInventorySnapshot(); } catch { /* non-fatal */ }

      setInventorySyncStatus((s) => ({ ...s, running: false }));
      markCompleted(LAST_INVENTORY_SYNC_KEY);
    } catch (e) {
      setInventorySyncStatus((s) => ({ ...s, running: false, error: e.message || "Inventory sync failed" }));
      markFailed(LAST_INVENTORY_SYNC_KEY);
    }
  }, []);

  // 180 days / 14-day windows = 13 chunks. Each chunk paginates its own
  // cursor sequentially (required — cursors only make sense relative to
  // the previous page), but the chunks themselves run concurrently, since
  // GET /v3/orders allows 5000 requests/min — nowhere near a limiting
  // factor at this volume.
  const ORDER_CHUNK_DAYS = 14;
  const ORDER_BACKFILL_DAYS = 180;
  const ORDER_CHUNK_CONCURRENCY = 4;

  const isoNoMs = (d) => d.toISOString().split('.')[0] + 'Z';

  const buildOrderDateChunks = () => {
    const chunks = [];
    const end = new Date();
    const start = new Date(end.getTime() - ORDER_BACKFILL_DAYS * 24 * 60 * 60 * 1000);
    let cursor = new Date(start);
    while (cursor < end) {
      const chunkEnd = new Date(Math.min(cursor.getTime() + ORDER_CHUNK_DAYS * 24 * 60 * 60 * 1000, end.getTime()));
      chunks.push([new Date(cursor), chunkEnd]);
      cursor = chunkEnd;
    }
    return chunks;
  };

  const runOrderSync = useCallback(async () => {
    setOrderSyncStatus({ running: true, sfProcessed: 0, sfTotal: null, wfsProcessed: 0, wfsTotal: null, error: "", relinkProgress: null });
    try {
      // One page of one 14-day chunk, retried on transient failure. Total
      // counts aren't meaningful when summed across independent date
      // windows, so only the running processed count is tracked here.
      const runChunkLoop = async (shipNode, chunkStart, chunkEnd, processedKey) => {
        let cursor = null;
        let done = false;
        let firstCall = true;
        while (!done) {
          const res = await withPageRetry(() => syncOrdersPage(shipNode, cursor, {
            isChunk: true,
            ...(firstCall ? { chunkStart, chunkEnd } : {}),
          }));
          firstCall = false;
          setOrderSyncStatus((s) => ({ ...s, [processedKey]: s[processedKey] + (res.received || 0) }));
          cursor = res.next_cursor;
          done = res.done;
        }
      };

      // Runs the full one-time 180-day backfill for a ship-node as parallel
      // 14-day chunks, then marks that node's watermark ONLY once every
      // chunk has finished — a single chunk finishing means nothing about
      // the overall backfill's completeness.
      const runShipNodeBackfill = async (shipNode, processedKey) => {
        const chunks = buildOrderDateChunks();
        for (let i = 0; i < chunks.length; i += ORDER_CHUNK_CONCURRENCY) {
          const batch = chunks.slice(i, i + ORDER_CHUNK_CONCURRENCY);
          await Promise.all(batch.map(([s, e]) =>
            runChunkLoop(shipNode, isoNoMs(s), isoNoMs(e), processedKey)
          ));
        }
        await markOrdersBackfilled(shipNode);
      };

      // Fast path for a ship-node that's already completed its one-time
      // backfill — plain sequential incremental sync via lastModifiedStartDate.
      const runShipNodeIncremental = async (shipNode, processedKey, totalKey) => {
        let cursor = null;
        let done = false;
        while (!done) {
          const res = await withPageRetry(() => syncOrdersPage(shipNode, cursor));
          setOrderSyncStatus((s) => ({
            ...s,
            [processedKey]: s[processedKey] + (res.received || 0),
            [totalKey]: res.total_count ?? s[totalKey],
          }));
          cursor = res.next_cursor;
          done = res.done;
        }
      };

      const status = await getOrderSyncStatus();

      await Promise.all([
        status.sf_synced_through
          ? runShipNodeIncremental('SellerFulfilled', 'sfProcessed', 'sfTotal')
          : runShipNodeBackfill('SellerFulfilled', 'sfProcessed'),
        status.wfs_synced_through
          ? runShipNodeIncremental('WFSFulfilled', 'wfsProcessed', 'wfsTotal')
          : runShipNodeBackfill('WFSFulfilled', 'wfsProcessed'),
      ]);

      // Kept for last_full_backfill_at bookkeeping only — correctness no
      // longer depends on it (per-node watermarks handle that now).
      await finalizeOrderSync();

      // Best-effort, non-fatal — re-links any order row whose
      // catalog_item_id came back null (SKU existed on Walmart's order
      // feed before it existed in our catalog_items yet). Purely additive:
      // only ever fills in currently-null links, never touches a row that
      // already has one, so it's safe to run after every single sync.
      // Paginated (500 rows/call) instead of one giant request — for an
      // account with tens of thousands of orphaned rows, one request could
      // take long enough to time out; this shows visible progress and
      // fails page-by-page instead of all-or-nothing.
      try {
        setOrderSyncStatus((s) => ({ ...s, relinkProgress: 0 }));
        const res = await withPageRetry(() => relinkOrphanedOrdersPage());
        setOrderSyncStatus((s) => ({ ...s, relinkProgress: res.relinked || 0 }));
      } catch (e) {
        console.warn("[runOrderSync] relink pass failed (non-fatal):", e.message);
      }

      setOrderSyncStatus((s) => ({ ...s, running: false }));
      markCompleted(LAST_ORDER_SYNC_KEY);
    } catch (e) {
      setOrderSyncStatus((s) => ({ ...s, running: false, error: e.message || "Order sync failed" }));
      markFailed(LAST_ORDER_SYNC_KEY);
    }
  }, []);

  // Fires the moment we KNOW Walmart is connected — never before. Syncing
  // against Walmart's API with no linked account is meaningless (every
  // call would just fail on WalmartAccountNotLinked), so this now waits
  // for walmartConnected === true instead of firing unconditionally on
  // mount. Still only ever fires once per session (ref guard), and once
  // it fires it covers every sidebar tab, not just Catalog.
  useEffect(() => {
    if (walmartConnected !== true) return;
    if (appSyncStartedRef.current) return; // guards React StrictMode double-invoke in dev, and re-fires of this effect
    appSyncStartedRef.current = true;

    // Run sequentially, not in parallel — all three hammering Supabase (and
    // Walmart's API) at the same time is what's causing intermittent
    // "Server disconnected" resets under concurrent HTTP/2 load. One at a
    // time is slightly slower end-to-end but far more reliable.
    (async () => {
      if (isStale(LAST_ITEM_SYNC_KEY)) await runItemsSync();
      // Orders always attempts a sync on every login now, not gated by
      // SYNC_STALE_MS — once both ship-nodes have completed their one-time
      // 180-day backfill (per-node watermark in order_sync_state), this is
      // just a fast lastModifiedStartDate catch-up, cheap enough to run
      // every session instead of waiting days between attempts.
      await runOrderSync();
      if (isStale(LAST_INVENTORY_SYNC_KEY)) await runInventorySync();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walmartConnected]);

  const resyncAll = async () => {
    await runItemsSync();
    await runOrderSync();
    await runInventorySync();
  };

  // Quick single-item add: ?singleItem=<walmartItemId> jumps straight to
  // Batches and skips the "resume batch" screen, prefilling the Item ID
  // search once the seller picks WFS/SF and names the batch. Only makes
  // sense once Walmart is actually connected — Batches is otherwise
  // inaccessible via the sidebar anyway.
  useEffect(() => {
    if (walmartConnected !== true) return;
    const params = new URLSearchParams(window.location.search);
    const singleItem = params.get("singleItem");
    if (singleItem) {
      setQuickAddItemId(singleItem);
      setSidebar("batches");
      setNavKey((k) => k + 1);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("singleItem");
      window.history.replaceState({}, "", clean.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walmartConnected]);

  // Pick up the Walmart connect handoff, whether the seller started from
  // our own "Connect Walmart" button or from Walmart's own Connect button
  // (Seller Center / Solution Provider Center). Either way they land here
  // with ?marketplace=walmart&walmartCallbackUri=... — we already know who
  // they are via authService, so just finish the handshake automatically.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const marketplace = params.get("marketplace");
    const walmartCallbackUri = params.get("walmartCallbackUri");

    if (marketplace === "walmart" && walmartCallbackUri) {
      const clientType = params.get("clientType");

      const clean = new URL(window.location.href);
      clean.searchParams.delete("marketplace");
      clean.searchParams.delete("walmartCallbackUri");
      clean.searchParams.delete("clientType");
      window.history.replaceState({}, "", clean.toString());

      // Not logged in yet? Stash the intent and send them to /login —
      // Login/Signup will bounce back here once they're authenticated.
      let storedCreds = null;
      try { storedCreds = localStorage.getItem("credentials"); } catch { /* ignore */ }
      if (!storedCreds) {
        try {
          sessionStorage.setItem(
            "msp_pending_walmart",
            JSON.stringify({ walmartCallbackUri, clientType })
          );
        } catch { /* ignore */ }
        window.location.href = "/login";
        return;
      }

      setSidebar("settings");

      authService
        .authenticatedFetch(`${API_BASE}/api/walmart/connect-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ walmartCallbackUri }),
        })
        .then((res) => res.json())
        .then((data) => { if (data.url) window.location.href = data.url; })
        .catch(() => { /* logged-out users are already redirected by authenticatedFetch */ });
    }
  }, []);

  const renderSidebarContent = () => {
    if (sidebar === "dashboard") return (
      <DashboardHome
        onNavigate={navigateTo}
        itemSyncStatus={itemSyncStatus}
        inventorySyncStatus={inventorySyncStatus}
        orderSyncStatus={orderSyncStatus}
        onSyncItems={runItemsSync}
        onSyncInventory={runInventorySync}
        onSyncOrders={runOrderSync}
        onSyncAll={resyncAll}
        walmartConnected={walmartConnected}
        onWalmartStatusChange={setWalmartConnected}
      />
    );
    // key={navKey} on every section forces a full remount whenever the
    // sidebar is clicked again — including re-clicking the section you're
    // already on — so any page-internal navigation state (e.g. which
    // report is currently open in Reports) resets back to that section's
    // default/home screen.
    if (sidebar === "batches") return <BatchesPage key={navKey} quickAddItemId={quickAddItemId} />;
    if (sidebar === "shipments") return <ShipmentsPage key={navKey} />;
    if (sidebar === "reports") return (
      <ReportsHubPage
        key={navKey}
        itemSyncStatus={itemSyncStatus}
        inventorySyncStatus={inventorySyncStatus}
        orderSyncStatus={orderSyncStatus}
      />
    );
    if (sidebar === "orders") return <OrdersPage key={navKey} />;
    if (sidebar === "returns") return <ReturnsPage key={navKey} />;
    if (sidebar === "reimbursements") return <ReimbursementsPage key={navKey} />;
    if (sidebar === "inventory") return (
      <CatalogPage
        key={navKey}
        itemSyncStatus={itemSyncStatus}
        itemSyncPopup={itemSyncPopup}
        setItemSyncPopup={setItemSyncPopup}
        inventorySyncStatus={inventorySyncStatus}
      />
    );
    if (sidebar === "settings") return <SettingsPage key={navKey} />;

    // Defensive fallback — sidebar navigation already blocks reaching any
    // other section while disconnected, but if `sidebar` somehow ends up
    // pointing at one anyway (e.g. stale state), show a clear prompt
    // instead of silently rendering a page that will just fail on every
    // Walmart call.
    if (walmartConnected !== true) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] flex-col gap-3 text-gray-400 px-6 text-center">
          <Lock size={40} className="opacity-40" />
          <p className="text-lg font-semibold text-gray-500">Connect your Walmart account to continue</p>
          <button onClick={() => navigateTo("dashboard")}
            className="mt-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold text-sm hover:opacity-90 transition">
            Go to Dashboard
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-[60vh] flex-col gap-3 text-gray-400">
        <Lock size={40} className="opacity-40" />
        <p className="text-lg font-semibold">Coming Soon!</p>
      </div>
    );
  };

  const syncIsRunning = itemSyncStatus.running || inventorySyncStatus.running || orderSyncStatus.running;
  const syncHasError = itemSyncStatus.error || inventorySyncStatus.error || orderSyncStatus.error;
  const [syncWidgetDismissed, setSyncWidgetDismissed] = useState(false);

  const itemSyncPercent = itemSyncStatus.totalItems
    ? Math.min(100, Math.round((itemSyncStatus.itemsSynced / itemSyncStatus.totalItems) * 100))
    : null;

  const inventorySyncPercent = (() => {
    const { sfProcessed, sfTotal, wfsProcessed, wfsTotal } = inventorySyncStatus;
    if (sfTotal === null || wfsTotal === null) return null;
    const totalProcessed = sfProcessed + wfsProcessed;
    const totalNeeded = sfTotal + wfsTotal;
    if (totalNeeded === 0) return null;
    return Math.min(100, Math.round((totalProcessed / totalNeeded) * 100));
  })();

  return (
    <div className="flex" style={{ minHeight: "calc(100vh - 64px)" }}>
      <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
            `}</style>

      <Sidebar
        active={sidebar} onNavigate={navigateTo} walmartConnected={walmartConnected}
        itemSyncStatus={itemSyncStatus} inventorySyncStatus={inventorySyncStatus} orderSyncStatus={orderSyncStatus}
        onSyncItems={runItemsSync} onSyncInventory={runInventorySync} onSyncOrders={runOrderSync} onSyncAll={resyncAll}
      />

      <div className="flex-1 bg-gradient-to-br from-slate-50 to-blue-50 overflow-y-auto">
        <div className="animate-fadeIn">{renderSidebarContent()}</div>
      </div>

      {/* Floating sync status — visible from anywhere in msp-inventory, not just Catalog */}
      {(syncIsRunning || syncHasError) && !syncWidgetDismissed && (
        <div className="fixed bottom-5 right-5 z-50 w-fit bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-2.5 py-1.5 bg-gray-50 border-b border-gray-100">
            <span className="text-xs font-bold text-gray-600 flex items-center gap-1.5 whitespace-nowrap">
              <RefreshCcw size={12} className={syncIsRunning ? "text-teal-600 animate-spin" : "text-red-500"} />
              {syncIsRunning ? "Syncing..." : "Sync issue"}
            </span>
            <button onClick={() => setSyncWidgetDismissed(true)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X size={13} />
            </button>
          </div>

          <div className="px-2.5 py-2 flex items-center gap-3 text-xs whitespace-nowrap">
            {(itemSyncStatus.running || itemSyncStatus.error) && (
              <span className="text-gray-600">
                Catalog: <span className={`font-semibold ${itemSyncStatus.error ? "text-red-500" : "text-gray-800"}`}>
                  {itemSyncStatus.error ? "Error" : itemSyncPercent !== null ? `${itemSyncPercent}%` : "..."}
                </span>
              </span>
            )}
            {(inventorySyncStatus.running || inventorySyncStatus.error) && (
              <span className="text-gray-600">
                Inventory: <span className={`font-semibold ${inventorySyncStatus.error ? "text-red-500" : "text-gray-800"}`}>
                  {inventorySyncStatus.error ? "Error" : inventorySyncPercent !== null ? `${inventorySyncPercent}%` : "..."}
                </span>
              </span>
            )}
            {(orderSyncStatus.running || orderSyncStatus.error) && (
              <span className="text-gray-600">
                Orders: <span className={`font-semibold ${orderSyncStatus.error ? "text-red-500" : "text-gray-800"}`}>
                  {orderSyncStatus.error
                    ? "Error"
                    : orderSyncStatus.relinkProgress !== null
                      ? `Finalizing... ${orderSyncStatus.relinkProgress} relinked`
                      : `SF ${orderSyncStatus.sfProcessed}${orderSyncStatus.sfTotal ? `/${orderSyncStatus.sfTotal}` : ""} · WFS ${orderSyncStatus.wfsProcessed}${orderSyncStatus.wfsTotal ? `/${orderSyncStatus.wfsTotal}` : ""}`}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MSPInventoryPage;